<script setup lang="ts">
/**
 * ChargeSelector — Charge picker grouped by charge type
 *
 * Props:
 *   visible — controls dialog visibility
 *   moduleTypeID — the module to load charges for
 *   currentChargeTypeID — currently loaded charge (for active state)
 *
 * Events:
 *   close — when dialog should close
 *   select — when a charge is selected (chargeTypeID)
 *   unload — when charge should be unloaded
 */
import { ref, computed, watch } from 'vue';
import { fetchChargesForModule, iconUrl } from '../../composables/useApi';
import type { Charge } from '../../../shared/types';

const props = defineProps<{
  visible: boolean;
  moduleTypeID: number;
  currentChargeTypeID: number | null;
}>();

const emit = defineEmits<{
  close: [];
  select: [chargeTypeID: number];
  unload: [];
}>();

const charges = ref<Charge[]>([]);
const loading = ref(false);
const searchQuery = ref('');

watch(() => props.visible, async (v) => {
  if (v && props.moduleTypeID) {
    loading.value = true;
    searchQuery.value = '';
    try {
      charges.value = await fetchChargesForModule(props.moduleTypeID);
    } catch {
      charges.value = [];
    } finally {
      loading.value = false;
    }
  }
});

// Group charges by group name
const groupedCharges = computed(() => {
  const query = searchQuery.value.toLowerCase();
  const filtered = query
    ? charges.value.filter(c => c.typeName.toLowerCase().includes(query))
    : charges.value;

  const groups = new Map<string, Charge[]>();
  for (const charge of filtered) {
    const key = charge.groupName || 'Other';
    const arr = groups.get(key);
    if (arr) {
      arr.push(charge);
    } else {
      groups.set(key, [charge]);
    }
  }
  return groups;
});

function handleSelect(chargeTypeID: number): void {
  emit('select', chargeTypeID);
  emit('close');
}

function handleKeydown(e: KeyboardEvent): void {
  if (e.key === 'Escape') {
    emit('close');
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
    <div v-if="visible" class="modal-overlay" @click.self="emit('close')" @keydown="handleKeydown">
      <div class="modal-dialog">
        <div class="modal-header">
          <h3 class="modal-title">Select Charge</h3>
          <button class="modal-close" @click="emit('close')">&times;</button>
        </div>

        <div class="modal-body">
          <input
            v-model="searchQuery"
            class="charge-search"
            type="text"
            placeholder="Search charges..."
            autofocus
          />

          <div v-if="loading" class="charge-loading">Loading charges...</div>

          <div v-else-if="charges.length === 0" class="charge-empty">
            No compatible charges found
          </div>

          <div v-else class="charge-groups">
            <!-- Unload option -->
            <button
              v-if="currentChargeTypeID"
              class="charge-item charge-item--unload"
              @click="emit('unload'); emit('close')"
            >
              Unload Current Charge
            </button>

            <template v-for="[groupName, groupCharges] in groupedCharges" :key="groupName">
              <div class="charge-group-header">{{ groupName }}</div>
              <button
                v-for="charge in groupCharges"
                :key="charge.typeID"
                class="charge-item"
                :class="{ 'charge-item--active': charge.typeID === currentChargeTypeID }"
                @click="handleSelect(charge.typeID)"
              >
                <img
                  :src="iconUrl(charge.typeID, 32)"
                  :alt="charge.typeName"
                  class="charge-icon"
                  loading="lazy"
                />
                <span class="charge-name">{{ charge.typeName }}</span>
                <span v-if="charge.typeID === currentChargeTypeID" class="charge-active">loaded</span>
              </button>
            </template>
          </div>
        </div>
      </div>
    </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modal-dialog {
  background: var(--bg-panel);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  width: 380px;
  max-width: 90vw;
  max-height: 70vh;
  display: flex;
  flex-direction: column;
  animation: modal-in 0.15s ease-out;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-color);
}

.modal-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
}

.modal-close:hover {
  color: var(--text-primary);
}

.modal-body {
  padding: 12px;
  overflow-y: auto;
  min-height: 0;
}

.charge-search {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  background: var(--bg-darker);
  color: var(--text-primary);
  font-size: 0.82rem;
  margin-bottom: 8px;
}

.charge-search:focus {
  outline: none;
  border-color: var(--accent-blue);
}

.charge-loading,
.charge-empty {
  text-align: center;
  color: var(--text-secondary);
  font-size: 0.82rem;
  padding: 24px 0;
}

.charge-groups {
  display: flex;
  flex-direction: column;
}

.charge-group-header {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--text-secondary);
  padding: 8px 8px 4px;
  letter-spacing: 0.04em;
}

.charge-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 8px;
  border: none;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--text-primary);
  font-size: 0.8rem;
  cursor: pointer;
  text-align: left;
  width: 100%;
  transition: background var(--transition-fast);
}

.charge-item:hover {
  background: rgba(74, 158, 255, 0.08);
}

.charge-item--active {
  background: rgba(74, 158, 255, 0.12);
}

.charge-item--unload {
  color: var(--accent-orange);
  font-size: 0.78rem;
  font-weight: 500;
  margin-bottom: 4px;
  padding: 6px 8px;
  border-bottom: 1px solid var(--border-color);
}

.charge-icon {
  width: 24px;
  height: 24px;
  border-radius: 2px;
  flex-shrink: 0;
}

.charge-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.charge-active {
  font-size: 0.68rem;
  color: var(--accent-blue);
  font-weight: 500;
  flex-shrink: 0;
}

/* Modal transitions */
.modal-enter-active { transition: opacity 0.2s ease; }
.modal-enter-active .modal-dialog { animation: modal-in 0.2s ease-out; }
.modal-leave-active { transition: opacity 0.15s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }

@keyframes modal-in {
  from { transform: translateY(-8px) scale(0.97); }
  to { transform: translateY(0) scale(1); }
}
</style>
